import { useEffect, useState, useCallback } from 'react';
import {
  getAllServersAdmin,
  createServer,
  updateServer,
  deleteServer,
  uploadServerImages, // NEW
} from '../../services/refurbishedServerService';
import './AdminRefurbishedServers.css';

const STATUS_OPTIONS = ['IN_STOCK', 'RESERVED', 'SOLD'];

const emptyForm = {
  name: '',
  brand: '',
  cpu: '',
  ram: '',
  storage: '',
  formFactor: '',
  description: '',
  originalPrice: '',
  salePrice: '',
  quantityAvailable: 1,
  status: 'IN_STOCK',
  images: [], // NEW
};

export default function AdminRefurbishedServers() {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploadingImages, setUploadingImages] = useState(false); // NEW

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getAllServersAdmin();
      setServers(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load listings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const onFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // NEW — uploads selected files immediately, appends returned URLs to
  // form.images. Uploading happens before Save so the admin sees previews
  // and can remove a bad one before submitting.
  const onImageSelect = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setError('');
    setUploadingImages(true);
    try {
      const data = await uploadServerImages(files);
      setForm((prev) => ({ ...prev, images: [...prev.images, ...(data.urls || [])] }));
    } catch (err) {
      setError(err?.response?.data?.message || 'Image upload failed');
    } finally {
      setUploadingImages(false);
      e.target.value = ''; // allow re-selecting the same file if needed
    }
  };

  const onRemoveImage = (urlToRemove) => {
    setForm((prev) => ({ ...prev, images: prev.images.filter((u) => u !== urlToRemove) }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    setError('');
    setBusy(true);
    try {
      const payload = {
        ...form,
        originalPrice: Number(form.originalPrice),
        salePrice: Number(form.salePrice),
        quantityAvailable: Number(form.quantityAvailable),
      };

      if (editingId) {
        const data = await updateServer(editingId, payload);
        setMsg(data?.message || 'Listing updated');
      } else {
        const data = await createServer(payload);
        setMsg(data?.message || 'Listing created');
      }
      resetForm();
      await load();
    } catch (e2) {
      setError(e2?.response?.data?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const onEdit = (s) => {
    setMsg('');
    setError('');
    setEditingId(s.id);
    setForm({
      name: s.name || '',
      brand: s.brand || '',
      cpu: s.cpu || '',
      ram: s.ram || '',
      storage: s.storage || '',
      formFactor: s.formFactor || '',
      description: s.description || '',
      originalPrice: s.originalPrice ?? '',
      salePrice: s.salePrice ?? '',
      quantityAvailable: s.quantityAvailable ?? 1,
      status: s.status || 'IN_STOCK',
      images: s.images || [], // NEW
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onDelete = async (s) => {
    if (!window.confirm(`Delete listing "${s.name}"?`)) return;
    setMsg('');
    setError('');
    setBusy(true);
    try {
      const data = await deleteServer(s.id);
      setMsg(data?.message || 'Listing deleted');
      if (editingId === s.id) resetForm();
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="as-page">
      <div className="as-container">
        <div className="as-topbar">
          <div>
            <h2 className="as-title">Refurbished Servers</h2>
            <p className="as-subtitle">Manage listings shown on the public storefront.</p>
          </div>
          <button className="as-btn" onClick={load} disabled={busy}>
            Refresh
          </button>
        </div>

        {msg ? <div className="as-alert as-alert--success">{msg}</div> : null}
        {error ? <div className="as-alert as-alert--error">{error}</div> : null}

        <div className="as-grid">
          {/* Form */}
          <section className="as-card">
            <h3>{editingId ? 'Edit listing' : 'Add listing'}</h3>

            <form onSubmit={onSubmit} className="as-form">
              <div>
                <div className="as-label">Name</div>
                <input
                  className="as-input"
                  placeholder="e.g. Dell R930 4U SFF Server"
                  value={form.name}
                  onChange={(e) => onFieldChange('name', e.target.value)}
                  required
                  disabled={busy}
                />
              </div>

              <div className="as-row">
                <div>
                  <div className="as-label">Brand</div>
                  <input
                    className="as-input"
                    placeholder="Dell, Quanta..."
                    value={form.brand}
                    onChange={(e) => onFieldChange('brand', e.target.value)}
                    disabled={busy}
                  />
                </div>
                <div>
                  <div className="as-label">Form factor</div>
                  <input
                    className="as-input"
                    placeholder="4U, 1U..."
                    value={form.formFactor}
                    onChange={(e) => onFieldChange('formFactor', e.target.value)}
                    disabled={busy}
                  />
                </div>
              </div>

              <div>
                <div className="as-label">CPU</div>
                <input
                  className="as-input"
                  placeholder="e.g. 4x Xeon E7-8880 v3, 72 Cores"
                  value={form.cpu}
                  onChange={(e) => onFieldChange('cpu', e.target.value)}
                  disabled={busy}
                />
              </div>

              <div className="as-row">
                <div>
                  <div className="as-label">RAM</div>
                  <input
                    className="as-input"
                    placeholder="768GB"
                    value={form.ram}
                    onChange={(e) => onFieldChange('ram', e.target.value)}
                    disabled={busy}
                  />
                </div>
                <div>
                  <div className="as-label">Storage</div>
                  <input
                    className="as-input"
                    placeholder="4x 2TB SSD"
                    value={form.storage}
                    onChange={(e) => onFieldChange('storage', e.target.value)}
                    disabled={busy}
                  />
                </div>
              </div>

              <div>
                <div className="as-label">Description</div>
                <textarea
                  className="as-textarea"
                  value={form.description}
                  onChange={(e) => onFieldChange('description', e.target.value)}
                  disabled={busy}
                />
              </div>

              <div className="as-row">
                <div>
                  <div className="as-label">Original price ($)</div>
                  <input
                    type="number"
                    step="0.01"
                    className="as-input"
                    value={form.originalPrice}
                    onChange={(e) => onFieldChange('originalPrice', e.target.value)}
                    required
                    disabled={busy}
                  />
                </div>
                <div>
                  <div className="as-label">Sale price ($)</div>
                  <input
                    type="number"
                    step="0.01"
                    className="as-input"
                    value={form.salePrice}
                    onChange={(e) => onFieldChange('salePrice', e.target.value)}
                    required
                    disabled={busy}
                  />
                </div>
              </div>

              <div className="as-row">
                <div>
                  <div className="as-label">Quantity available</div>
                  <input
                    type="number"
                    min="0"
                    className="as-input"
                    value={form.quantityAvailable}
                    onChange={(e) => onFieldChange('quantityAvailable', e.target.value)}
                    disabled={busy}
                  />
                </div>
                <div>
                  <div className="as-label">Status</div>
                  <select
                    className="as-select"
                    value={form.status}
                    onChange={(e) => onFieldChange('status', e.target.value)}
                    disabled={busy}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <div className="as-label">Images</div>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  multiple
                  onChange={onImageSelect}
                  disabled={busy || uploadingImages}
                />
                {uploadingImages ? (
                  <div className="as-note">Uploading...</div>
                ) : null}

                {form.images.length > 0 ? (
                  <div className="as-image-previews">
                    {form.images.map((url) => (
                      <div key={url} className="as-image-preview">
                        <img src={url} alt="" />
                        <button
                          type="button"
                          className="as-image-remove"
                          onClick={() => onRemoveImage(url)}
                          disabled={busy || uploadingImages}
                          aria-label="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="as-actions">
                <button className="as-btn as-btn--primary" type="submit" disabled={busy}>
                  {editingId ? 'Update listing' : 'Add listing'}
                </button>
                {editingId ? (
                  <button className="as-btn" type="button" onClick={resetForm} disabled={busy}>
                    Cancel
                  </button>
                ) : null}
              </div>
            </form>
          </section>

          {/* List */}
          <section className="as-card">
            <h3>All listings ({servers.length})</h3>

            {loading ? <div className="as-alert">Loading...</div> : null}

            <div className="as-list">
              {servers.map((s) => (
                <div key={s.id} className="as-item">
                  <div className="as-item-top">
                    <div>
                      <strong>{s.name}</strong>
                      <div className="as-item-meta">
                        ${s.salePrice.toLocaleString()} (was ${s.originalPrice.toLocaleString()}) ·{' '}
                        {s.quantityAvailable} available · {s.status.replace('_', ' ')}
                      </div>
                    </div>
                    <div className="as-row-btns">
                      <button className="as-btn as-btn--sm" onClick={() => onEdit(s)} disabled={busy}>
                        Edit
                      </button>
                      <button
                        className="as-btn as-btn--sm as-btn--danger"
                        onClick={() => onDelete(s)}
                        disabled={busy}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {!loading && servers.length === 0 ? (
                <div className="as-alert">No listings yet. Add your first one above.</div>
              ) : null}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}